import { TooltipHoverTarget } from "@repo/ui/tooltip/hoverTarget";
import { SimpleTooltip } from "@repo/ui/tooltip/simpleTooltip";
import { TooltipTrigger } from "@repo/ui/tooltip/tooltipTrigger";
import type { FC, ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { renderDuration } from "./durationFormatting.js";

interface CriterionTimeBarProps {
  /** Teammate's average coverage seconds for this row. */
  readonly teammateAverageSeconds: number | undefined;
  /**
   * Top-10 performers' benchmark for this row. Omitted when the playbook has
   * no outcome definition — the tick disappears, the filled bar still shows.
   */
  readonly topPerformerAverageSeconds: number | undefined;
  /**
   * Shared time scale across the whole table — full track width represents
   * this many seconds. Values beyond clamp to the right edge.
   */
  readonly maxSeconds: number;
}

// Compact track tuned for dense table rows — short enough not to dominate the
// column, tall enough for a readable tick mark. Mirrors PR 2918's
// criterionTimePill.tsx.
const TRACK_WIDTH_PX = 72;
const TRACK_HEIGHT_PX = 6;
const TICK_HEIGHT_PX = 10;

// Outlier thresholds: the bar flips from neutral grey to red only when the
// teammate is over the top-performer benchmark by BOTH a large relative
// margin (>40%) and a large absolute margin (>3 minutes).
const RED_RATIO = 1.4;
const RED_ABSOLUTE_SECONDS = 180;

/**
 * Pick the bar fill color from the teammate's average vs. the top-performer
 * benchmark. Defaults to grey; turns red only for clear outliers.
 */
const getBarColorClass = (
  teammateAverageSeconds: number,
  topPerformerAverageSeconds: number | undefined,
): string => {
  if (
    topPerformerAverageSeconds === undefined ||
    topPerformerAverageSeconds <= 0
  ) {
    return "bg-slate-400";
  }
  const overSeconds = teammateAverageSeconds - topPerformerAverageSeconds;
  const overRatio = teammateAverageSeconds / topPerformerAverageSeconds;
  if (overSeconds > RED_ABSOLUTE_SECONDS && overRatio > RED_RATIO) {
    return "bg-red-500";
  }
  return "bg-slate-400";
};

/**
 * Per-row time indicator: a duration label on the left and a horizontal bar
 * showing the teammate's coverage with a tick mark for the top-10 performer
 * benchmark. The bar uses a caller-supplied shared scale (`maxSeconds`) so
 * longer fill always means more time spent across rows.
 *
 * When a top-performer benchmark is available, the whole pill is wrapped in
 * a tooltip showing the benchmark value.
 */
export const CriterionTimeBar: FC<CriterionTimeBarProps> = ({
  teammateAverageSeconds,
  topPerformerAverageSeconds,
  maxSeconds,
}) => {
  if (teammateAverageSeconds === undefined) {
    return null;
  }

  const actualMaxSeconds = Math.max(
    maxSeconds,
    teammateAverageSeconds,
    topPerformerAverageSeconds ?? 0,
  );

  const barPct =
    actualMaxSeconds > 0
      ? Math.min((teammateAverageSeconds / actualMaxSeconds) * 100, 100)
      : 0;

  const tickPct =
    topPerformerAverageSeconds !== undefined && actualMaxSeconds > 0
      ? Math.min((topPerformerAverageSeconds / actualMaxSeconds) * 100, 100)
      : undefined;

  const barColorClass = getBarColorClass(
    teammateAverageSeconds,
    topPerformerAverageSeconds,
  );

  const pillContents: ReactNode = (
    <>
      <span className="relative shrink-0 text-right text-xs text-muted-foreground tabular-nums">
        <span className="invisible">0:00:00</span>
        <span className="absolute inset-0">
          {renderDuration(teammateAverageSeconds)}
        </span>
      </span>
      <div
        className="relative shrink-0 rounded-full bg-gray-200"
        style={{ width: TRACK_WIDTH_PX, height: TRACK_HEIGHT_PX }}
        aria-hidden
      >
        {/* Teammate's time as a filled bar from the left edge. */}
        <div
          className={twMerge("h-full rounded-full", barColorClass)}
          // Ensure very short bars are still visible.
          style={{ width: `${Math.max(barPct, 3)}%` }}
        />
        {tickPct !== undefined && (
          // Top-performer benchmark as a vertical tick that pokes above and
          // below the bar for contrast. Centered on its x position.
          <div
            className="absolute top-1/2 w-[2px] -translate-x-1/2 -translate-y-1/2 rounded-xs bg-slate-900"
            style={{ left: `${tickPct}%`, height: TICK_HEIGHT_PX }}
          />
        )}
      </div>
    </>
  );

  if (topPerformerAverageSeconds === undefined) {
    // No benchmark → nothing meaningful to compare. Skip the tooltip wrapper
    // so we don't add a hover affordance that says "Top agents avg —".
    return <div className="inline-flex items-center gap-2">{pillContents}</div>;
  }

  return (
    <TooltipTrigger>
      <TooltipHoverTarget className="gap-2">{pillContents}</TooltipHoverTarget>
      <SimpleTooltip>
        Top agents avg {renderDuration(topPerformerAverageSeconds)}
      </SimpleTooltip>
    </TooltipTrigger>
  );
};
